import * as THREE from 'three';

// ── A RESIDENT'S LAMPS, LIT INSIDE THE RESIDENT'S OWN SHADER ──────────────────
//
// Every rigged resident carries a key and a fill (a rim too, indoors) — Khari's
// calibrated look, KEY 3 / FILL 2 above the head, tuned by eye on 2026-08-02.
// They used to be real <pointLight>s, and in three.js a point light is not
// attached to anything: EVERY lit material in the scene loops over EVERY point
// light for EVERY pixel it draws. Thirteen residents meant twenty-six lights in
// the fragment shader of every building, road and billboard in the city.
//
// Measured on the street at night (M2 Max, GPU timer queries, 2026-09-23):
//   lamps as scene lights   ~37 ms GPU per frame
//   ?lit=0                  ~5 ms
// And because the light COUNT is compiled into every shader, a resident walking
// into range or out of it recompiled every material in the city — the multi-
// second hitches on arrival.
//
// A lamp's light only ever needed to land on its own figure (the pool it left
// on the pavement was called out as a bug — see the calibration notes in
// AnimatedResident). So the SAME lamps are evaluated with the SAME maths — three's
// punctual-light falloff and its own RE_Direct BRDF — but only in the shaders of
// that resident's materials. The figure is lit exactly as before; nothing else
// pays for it, and the scene's light count never changes.

export const MAX_LAMPS = 3;

const DECL = /* glsl */`
uniform vec3 uLampPosW[${MAX_LAMPS}];
uniform vec3 uLampCol[${MAX_LAMPS}];
uniform float uLampDist[${MAX_LAMPS}];
uniform int uLampN;
`;

// Mirrors getPointLightInfo + getDistanceAttenuation (decay 2, with cutoff),
// then hands the light to the material's own RE_Direct — the same call three
// makes for a real point light.
const APPLY = /* glsl */`
#ifdef RE_Direct
for ( int li = 0; li < ${MAX_LAMPS}; li ++ ) {
	if ( li >= uLampN ) break;
	vec3 lampPos = ( viewMatrix * vec4( uLampPosW[ li ], 1.0 ) ).xyz;
	vec3 lampVec = lampPos - geometryPosition;
	float lampD = length( lampVec );
	IncidentLight lampLight;
	lampLight.direction = normalize( lampVec );
	float lampFall = 1.0 / max( lampD * lampD, 0.01 );
	if ( uLampDist[ li ] > 0.0 ) lampFall *= pow2( saturate( 1.0 - pow4( lampD / uLampDist[ li ] ) ) );
	lampLight.color = uLampCol[ li ] * lampFall;
	lampLight.visible = true;
	RE_Direct( lampLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight );
}
#endif
`;

/** One set of lamp uniforms per resident, shared by all of that resident's materials. */
export function createLampUniforms() {
  return {
    uLampPosW: { value: Array.from({ length: MAX_LAMPS }, () => new THREE.Vector3()) },
    uLampCol: { value: Array.from({ length: MAX_LAMPS }, () => new THREE.Color(0, 0, 0)) },
    uLampDist: { value: new Array(MAX_LAMPS).fill(1) },
    uLampN: { value: 0 },
  };
}

/**
 * Patch a lit material so it evaluates `uniforms`' lamps. Idempotent per
 * material. Chains any onBeforeCompile already on it (the face decal's chroma
 * key has one) and keeps its program cache key distinct from unpatched copies.
 */
export function patchLampMaterial(material, uniforms) {
  if (!material || material.userData.__lamps === uniforms) return false;
  if (!(material.isMeshStandardMaterial || material.isMeshPhongMaterial || material.isMeshLambertMaterial || material.isMeshToonMaterial)) return false;
  // Re-patching for a different resident would chain two lamp blocks; a
  // material belongs to one body, so that is a caller bug worth hearing about.
  if (material.userData.__lamps) { console.warn('[lamps] material already lit by another resident — clone it per body'); return false; }
  const prev = material.onBeforeCompile;
  const prevKey = material.customProgramCacheKey();
  material.onBeforeCompile = function (shader, renderer) {
    prev?.call(this, shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${DECL}`)
      .replace('#include <lights_fragment_begin>', `#include <lights_fragment_begin>\n${APPLY}`);
  };
  material.customProgramCacheKey = () => `${prevKey}|lamps${MAX_LAMPS}`;
  material.userData.__lamps = uniforms;
  material.needsUpdate = true;
  return true;
}

const V = new THREE.Vector3();
/**
 * Write this frame's lamps. `lamps` = [{ pos:[x,y,z] (in `anchor`'s local
 * space), intensity, distance, color }]; `anchor` = the object they ride on.
 */
export function setLamps(uniforms, anchor, lamps) {
  const n = Math.min(lamps.length, MAX_LAMPS);
  uniforms.uLampN.value = n;
  for (let i = 0; i < n; i++) {
    const l = lamps[i];
    anchor.localToWorld(uniforms.uLampPosW.value[i].copy(V.set(l.pos[0], l.pos[1], l.pos[2])));
    uniforms.uLampCol.value[i].set(l.color).multiplyScalar(l.intensity);
    uniforms.uLampDist.value[i] = l.distance;
  }
}
